import { MONETATE_DEFAULT_VARIANT } from './monetate';

export interface MonetateVariant extends Record<string, string> {
  default: typeof MONETATE_DEFAULT_VARIANT;
}

export interface MonetateExperiment {
  experiment: string;
  variant: MonetateVariant;
}

export const URGENCY_BANNER = {
  experiment: 'urgencyBanner',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    danger: 'danger',
    warning: 'warning',
    info: 'info',
  },
} as const satisfies MonetateExperiment;

export const NAV_LINKS_TRENDING = {
  experiment: 'navLinksTrending',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showTrending: 'showTrending',
  },
} as const satisfies MonetateExperiment;

export const POD_SLIDEDOWN_ADVERT_RELATED = {
  experiment: 'podSlidedownAdvertRelated',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showAdvertImage: 'showAdvertImage',
    showAdvertLogo: 'showAdvertLogo',
  },
  // provides following payload:
  //
  // {
  //    productIds: [],
  //    imageUrl?: "",
  //    logoUrl?: "",
  //    text: "",
  //    url: "",
  // }
} as const satisfies MonetateExperiment;

export const POD_SLIDEDOWN_ADVERT_GOES_WELL_WITH = {
  experiment: 'podSlidedownAdvertGoesWellWith',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showAdvertImage: 'showAdvertImage',
    showAdvertIcon: 'showAdvertIcon',
  },
  // provides following payload:
  //
  // {
  //    productIds: [],
  //    imageUrl?: "",
  //    iconName?: "",
  //    text: "",
  //    url: "",
  // }
} as const satisfies MonetateExperiment;

export const SHOW_PRODUCT_TAGS = {
  experiment: 'productTags',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showTags: 'showTags',
    hideTags: 'hideTags',
  },
} as const satisfies MonetateExperiment;

export const SHOW_MARKETING_BADGES = {
  experiment: 'showMarketingBadges',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showBadges: 'showBadges',
  },
} as const satisfies MonetateExperiment;

export const TROLLEY_PROMO_BADGE = {
  experiment: 'trolleyPromoBadgeV2',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    displayPromoBadgingV2: 'displayPromoBadgingV2',
  },
} as const satisfies MonetateExperiment;

export const EASTER_MODAL_EXPERIMENT = {
  experiment: 'easterModal',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    displayEasterModal: 'displayEasterModal',
  },
} as const satisfies MonetateExperiment;

export const TROLLEY_RECOMMENDATIONS = {
  experiment: 'trolley_recommendations',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    displayRecommendations: 'displayRecommendations',
  },
} as const satisfies MonetateExperiment;

export const SLOT_SFO_ADDRESS_SELECTOR = {
  experiment: 'slots_sfoAddressSelector',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    show: 'show',
  },
} as const satisfies MonetateExperiment;

export const SLOT_NEW_BOOKING_EXPERIENCE = {
  experiment: 'slots_newBookingExperience',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    show: 'show',
  },
} as const satisfies MonetateExperiment;

export const SLOTS_SERVICE_SELECTION_PAGE = {
  experiment: 'slots_serviceSelectionPage',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    skip: 'skip',
  },
} as const satisfies MonetateExperiment;

export const CONTENT_VISIBILITY_FOOTER = {
  experiment: 'build_contentVisibilityFooter',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    lazyLoadFooter: 'lazyLoadFooter',
  },
} as const satisfies MonetateExperiment;

export const EOS_GLP_NAV = {
  experiment: 'EoS_glpNavigation',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showMenu: 'showMenu',
    showMenuWithBackground: 'showMenuWithBackground',
  },
} as const satisfies MonetateExperiment;

export const AEM_MISSION_ONBOARDING_HOMEPAGE_CONTENT = {
  experiment: 'aem_missionOnboardingHomepageContent',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showOnboarding: 'showOnboarding',
    showOnboardingNew: 'showOnboardingNew',
    showOnboardingLoggedInDormant: 'showOnboardingLoggedInDormant',
    showOnboardingNoOrders: 'showOnboardingNoOrders',
    showOnboardingLoggedOutDormant: 'showOnboardingLoggedOutDormant',
  },
} as const satisfies MonetateExperiment;

export const CHECKOUT_WISMO_TRIAL = {
  experiment: 'checkoutWismoTrial',
  variant: { default: MONETATE_DEFAULT_VARIANT },
  // payload: "customer,id,list"
} as const satisfies MonetateExperiment;

export const MY_WAITROSE_MARKETING_PREFERENCES = {
  experiment: 'myWaitrose_marketingPreferences',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    allLOB: 'allLOB',
    pucc: 'pucc',
  },
} as const satisfies MonetateExperiment;

export const EOS_NEW_HEADER = {
  experiment: 'EoS_newHeader',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showNewHeader: 'showNewHeader',
  },
} as const satisfies MonetateExperiment;

export const EOS_NEW_HEADER_WITH_WARRANT = {
  experiment: 'EoS_newHeaderWithWarrant',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    withWarrant: 'withWarrant',
  },
} as const satisfies MonetateExperiment;

export const BUILD_QUICK_SHOP_ONE_PAGER = {
  experiment: 'build_quickShopOnePager',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showOnePager: 'showOnePager',
  },
} as const satisfies MonetateExperiment;

export const BUILD_FAVOURITES_LEADERBOARD_BANNER = {
  experiment: 'build_favouritesLeaderboardBanner',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    showBanner: 'showBanner',
  },
  // provides following payload:
  // { keyword: "" }
} as const satisfies MonetateExperiment;

export const SEARCH_TERM_SUGGEST = {
  experiment: 'search_unbxdAutosuggestv1',
  variant: {
    es: 'es',
    unbxd: 'unbxd',
    default: MONETATE_DEFAULT_VARIANT,
  },
} as const satisfies MonetateExperiment;

export const SHOP_BY_LIST = {
  experiment: 'agenticShopping_shopByList',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    add: 'add',
    preselect: 'preselect',
  },
} as const satisfies MonetateExperiment;

export const DYNAMIC_SEARCH_TERMS = {
  experiment: 'search_dynamicSearchTerms',
  variant: {
    default: MONETATE_DEFAULT_VARIANT,
    dynamic: 'dynamic',
  },
} as const satisfies MonetateExperiment;

// Register here the experiments
const allExperimentsList: [string, MonetateExperiment][] = [
  [URGENCY_BANNER.experiment, URGENCY_BANNER],
  [NAV_LINKS_TRENDING.experiment, NAV_LINKS_TRENDING],
  [SHOW_PRODUCT_TAGS.experiment, SHOW_PRODUCT_TAGS],
  [SHOW_MARKETING_BADGES.experiment, SHOW_MARKETING_BADGES],
  [POD_SLIDEDOWN_ADVERT_RELATED.experiment, POD_SLIDEDOWN_ADVERT_RELATED],
  [POD_SLIDEDOWN_ADVERT_GOES_WELL_WITH.experiment, POD_SLIDEDOWN_ADVERT_GOES_WELL_WITH],
  [TROLLEY_PROMO_BADGE.experiment, TROLLEY_PROMO_BADGE],
  [EASTER_MODAL_EXPERIMENT.experiment, EASTER_MODAL_EXPERIMENT],
  [TROLLEY_RECOMMENDATIONS.experiment, TROLLEY_RECOMMENDATIONS],
  [SLOT_SFO_ADDRESS_SELECTOR.experiment, SLOT_SFO_ADDRESS_SELECTOR],
  [SLOT_NEW_BOOKING_EXPERIENCE.experiment, SLOT_NEW_BOOKING_EXPERIENCE],
  [SLOTS_SERVICE_SELECTION_PAGE.experiment, SLOTS_SERVICE_SELECTION_PAGE],
  [CONTENT_VISIBILITY_FOOTER.experiment, CONTENT_VISIBILITY_FOOTER],
  [EOS_GLP_NAV.experiment, EOS_GLP_NAV],
  [AEM_MISSION_ONBOARDING_HOMEPAGE_CONTENT.experiment, AEM_MISSION_ONBOARDING_HOMEPAGE_CONTENT],
  [CHECKOUT_WISMO_TRIAL.experiment, CHECKOUT_WISMO_TRIAL],
  [MY_WAITROSE_MARKETING_PREFERENCES.experiment, MY_WAITROSE_MARKETING_PREFERENCES],
  [EOS_NEW_HEADER.experiment, EOS_NEW_HEADER],
  [BUILD_QUICK_SHOP_ONE_PAGER.experiment, BUILD_QUICK_SHOP_ONE_PAGER],
  [BUILD_FAVOURITES_LEADERBOARD_BANNER.experiment, BUILD_FAVOURITES_LEADERBOARD_BANNER],
  [SEARCH_TERM_SUGGEST.experiment, SEARCH_TERM_SUGGEST],
  [SHOP_BY_LIST.experiment, SHOP_BY_LIST],
  [DYNAMIC_SEARCH_TERMS.experiment, DYNAMIC_SEARCH_TERMS],
] as const;

const allExperiments = new Map<string, MonetateExperiment>(allExperimentsList);

const monetateExperimentIds = [...allExperiments.keys()];

export const getMonetateExperimentIds = () => monetateExperimentIds;
export const getMonetateExperimentById = (id: string) => allExperiments.get(id);
export const matchDecisionByExperiment = (id: string, variantDecision: string) =>
  id
    ? getMonetateExperimentById(id)?.variant[variantDecision] || MONETATE_DEFAULT_VARIANT
    : MONETATE_DEFAULT_VARIANT;

export type Experiments = EntriesToObject<typeof allExperimentsList>;

export type GetVariantsByExperiment<T extends keyof Experiments> =
  Experiments[T]['variant'][keyof Experiments[T]['variant']];
